import { useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { userHiddenClasses } from '../graphQLOperations/ClassCategories';
import { ClassCategoriesUserHiddenClassesQuery } from '../graphQLOperations/__generated__/ClassCategoriesUserHiddenClassesQuery.graphql';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';
import { selectHiddenClassOverrides } from '../redux/reducers/hiddenClassesSlice';

/**
 * IDs of the current user's hidden classes, used to keep them out of class dropdowns.
 * Combines the backend state with session overrides (Redux) so hide/unhide reflect
 * instantly everywhere without a refetch.
 */
export const useHiddenClassIds = (skip = false): Set<string> => {
  const userRedux = useAppSelector(selectUser);
  const overrides = useAppSelector(selectHiddenClassOverrides);
  const publicUserId = userRedux?.sub ?? '';

  const data = useLazyLoadQuery<ClassCategoriesUserHiddenClassesQuery>(
    userHiddenClasses,
    { publicUserId, skip: skip || !publicUserId },
    { fetchPolicy: 'store-or-network' },
  );

  return useMemo(() => {
    const hidden = new Set<string>();
    (data.userByPublicUserId?.userClassCategories ?? []).forEach(category => {
      if (category?.category === 'HIDDEN') hidden.add(category.classId);
    });
    // Session overrides win so a hide/unhide this session shows immediately.
    Object.entries(overrides ?? {}).forEach(([classId, isHidden]) =>
      isHidden ? hidden.add(classId) : hidden.delete(classId),
    );
    return hidden;
  }, [data, overrides]);
};
